import {
  AloftArea,
  AloftPrice,
  CraftsmanCostProps,
  getCraftsmanCost,
  isUndefined,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { selectEffectiveDate } from '../orderSelectors.ts';
import { selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectPropertyIdentificationEntities } from '../propertyDataSelectors/propertyIdentificationSelectors.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { selectCostProps } from './selectCostProps.ts';

const calculateSFRCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  effectiveDate: ReturnType<typeof selectEffectiveDate>,
  id: string,
) => {
  const improvementsForTransaction = improvements[id];
  const propertyIdentificationForTransaction = propertyIdentification[id];

  if (
    isUndefined(improvementsForTransaction) ||
    isUndefined(propertyIdentificationForTransaction) ||
    isUndefined(effectiveDate)
  ) {
    return undefined;
  }

  const { gla, corners, craftsmanQuality: quality } = improvementsForTransaction;
  const {
    address: { PostalCode: zipCode },
  } = propertyIdentificationForTransaction;

  if (
    isUndefined(zipCode) ||
    isUndefined(gla) ||
    isUndefined(quality) ||
    (quality * 10) % 1 !== 0 ||
    quality < 1
  ) {
    return undefined;
  }

  const calculator = getCraftsmanCost(costProps);

  return calculator.getSFRCost({
    quality,
    sqft: AloftArea.schema.parse(gla),
    zipCode,
    corners,
  });
};

const getTotalSFRCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  effectiveDate: ReturnType<typeof selectEffectiveDate>,
  id: string,
) => {
  const costOutput = calculateSFRCost(
    costProps,
    improvements,
    propertyIdentification,
    effectiveDate,
    id,
  );

  if (costOutput === undefined) {
    return AloftPrice.zero;
  }

  return costOutput.totalCost;
};

export const selectSFRCost = createPriceSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    selectEffectiveDate,
    (_state, id: string) => id,
  ],
  getTotalSFRCost,
);

export const selectSFRCostOutput = createSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    selectEffectiveDate,
    (state, id: string) => id,
  ],
  calculateSFRCost,
);

export const selectSFRCostDisplay = createSelector([selectSFRCost], (SFRCost) =>
  SFRCost?.toString(true),
);
